import {
  Boxes,
  ChevronRight,
  Folder,
  FolderOpen,
  ListChecks,
  Search,
  Server,
  Unlink,
  Wrench
} from 'lucide-react'
import type { TagInventoryFolder, TagInventoryParent } from '@axilon-apps/domains-ontology-ui-state'

import { cn } from '../../lib/utils'

export interface TagTreeBreadcrumb {
  id: string
  label: string
}

export interface TagTreeSidebarProps {
  parent?: TagInventoryParent
  folders: TagInventoryFolder[]
  breadcrumbs: TagTreeBreadcrumb[]
  countUnavailableIds?: ReadonlySet<string>
  parentCountUnavailable?: boolean
  isLoading?: boolean
  filtersActive?: boolean
  onNavigate: (folder: TagInventoryFolder) => void
  onSelectAll?: (folder: TagInventoryFolder) => void
  onNavigateBreadcrumb: (index: number) => void
  activeView?: 'tree' | 'search' | 'issues'
  searchText: string
  onSearch: () => void
  onSearchTextChange: (value: string) => void
  onRegistrationIssues?: () => void
  registrationIssuesCount?: number | null
  onOpenTools?: () => void
}

const SECTION_HEADING_CLASS = 'font-sans text-9 font-semibold uppercase tracking-wide text-ink-3'

function FolderRow({
  folder,
  countUnavailable,
  onNavigate,
  onSelectAll
}: {
  folder: TagInventoryFolder
  countUnavailable?: boolean
  onNavigate: (folder: TagInventoryFolder) => void
  onSelectAll?: (folder: TagInventoryFolder) => void
}) {
  const provider = folder.kind === 'provider'
  const udt = folder.kind === 'udt'
  const selectable = !countUnavailable && folder.subtreeTagCount > 0
  return (
    <div className="group flex items-center rounded hover:bg-bg-sunk/60">
      <button
        type="button"
        role="treeitem"
        aria-selected={false}
        onClick={() => onNavigate(folder)}
        data-testid={`tag-tree-node-${folder.id}`}
        className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-2 py-1.5 text-left font-mono text-xs text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {provider ? (
          <Server className="size-3 shrink-0 text-ink-3" aria-hidden />
        ) : udt ? (
          <Boxes className="size-3 shrink-0 text-brand" aria-hidden />
        ) : (
          <Folder className="size-3 shrink-0 text-ink-3" aria-hidden />
        )}
        <span className="min-w-0 flex-1 truncate">{folder.name}</span>
        {udt ? (
          <span
            className="shrink-0 rounded bg-brand-soft px-1 py-0.5 font-sans text-8 font-semibold uppercase text-brand"
            data-testid={`tag-tree-udt-${folder.id}`}
          >
            UDT
          </span>
        ) : null}
        {folder.folderless ? (
          <span
            className="shrink-0 rounded bg-bg-sunk px-1 py-0.5 font-sans text-8 uppercase text-ink-3"
            data-testid={`tag-tree-folderless-${folder.id}`}
          >
            no folders
          </span>
        ) : null}
        <span
          className="shrink-0 text-10 text-ink-3"
          title={
            countUnavailable
              ? 'The matching tag count is unavailable for this subtree.'
              : `${folder.subtreeTagCount.toLocaleString()} tags in this subtree`
          }
        >
          {countUnavailable ? '—' : folder.subtreeTagCount.toLocaleString()}
        </span>
        <ChevronRight className="size-3 shrink-0 text-ink-3" aria-hidden />
      </button>
      {onSelectAll && selectable ? (
        <button
          type="button"
          onClick={() => onSelectAll(folder)}
          aria-label={`Select all tags in ${folder.name}`}
          title={`Select all ${folder.subtreeTagCount.toLocaleString()} tags in ${folder.name}, including nested folders`}
          data-testid={`tag-tree-select-all-${folder.id}`}
          className="ml-0.5 mr-1 shrink-0 rounded p-1.5 text-ink-3 opacity-60 hover:bg-brand-soft hover:text-brand focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
        >
          <ListChecks className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}

const TagTreeSidebar = ({
  parent,
  folders,
  breadcrumbs,
  countUnavailableIds = new Set(),
  parentCountUnavailable = false,
  isLoading = false,
  filtersActive = false,
  onNavigate,
  onSelectAll,
  onNavigateBreadcrumb,
  activeView = 'tree',
  searchText,
  onSearch,
  onSearchTextChange,
  onRegistrationIssues,
  registrationIssuesCount,
  onOpenTools
}: TagTreeSidebarProps) => {
  const displayedCount =
    parent?.kind === 'providers'
      ? folders.reduce((count, folder) => count + folder.subtreeTagCount, 0)
      : (parent?.subtreeTagCount ?? 0)
  const displayedCountUnavailable =
    parent?.kind === 'providers'
      ? folders.some((folder) => countUnavailableIds.has(folder.id))
      : false

  return (
    <aside
      className="flex w-66 shrink-0 flex-col border-r border-line bg-bg-elev"
      aria-label="Tags"
    >
      <div className="px-3.5 pb-1 pt-3">
        <div className="flex min-h-7 items-center gap-2">
          <h2 className="m-0 text-14 font-bold uppercase tracking-wider text-ink">Tags</h2>
          <span className="font-mono text-11 text-ink-3">
            {displayedCountUnavailable ? '—' : displayedCount.toLocaleString()}
          </span>
          <div className="flex-1" />
        </div>
        <label
          className={cn(
            'mt-2 flex items-center gap-1.5 rounded-md border bg-bg px-2 py-1.5 transition-colors focus-within:ring-2 focus-within:ring-ring',
            activeView === 'search' ? 'border-brand/50 bg-brand-soft/40' : 'border-line'
          )}
        >
          <Search className="size-3.5 shrink-0 text-ink-3" aria-hidden />
          <input
            type="search"
            value={searchText}
            onFocus={onSearch}
            onChange={(event) => onSearchTextChange(event.target.value)}
            aria-label="Search tags"
            placeholder="Search names and paths…"
            className="min-w-0 flex-1 bg-transparent text-xs text-ink outline-none placeholder:text-ink-3"
            data-testid="tags-search-input"
          />
        </label>
        <nav className="mt-2 flex min-w-0 items-center gap-1" aria-label="Tags breadcrumb">
          {breadcrumbs.map((crumb, index) => {
            const isCurrent = index === breadcrumbs.length - 1
            const className = cn(
              'min-w-0 truncate rounded px-1 py-0.5',
              !isCurrent && 'hover:bg-bg-sunk',
              index === 0
                ? SECTION_HEADING_CLASS
                : cn('font-mono text-10', isCurrent ? 'text-ink' : 'text-ink-3')
            )
            return (
              <span key={`${crumb.id}-${index}`} className="contents">
                {index > 0 ? <ChevronRight className="size-3 shrink-0 text-ink-3" /> : null}
                {isCurrent ? (
                  <span className={className} aria-current="page">
                    {crumb.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onNavigateBreadcrumb(index)}
                    className={className}
                  >
                    {crumb.label}
                  </button>
                )}
              </span>
            )
          })}
        </nav>
      </div>

      {parent && parent.kind !== 'providers' ? (
        <div
          className="flex items-center gap-2 border-b border-line bg-bg-sunk/40 px-3.5 py-2"
          data-testid="tag-tree-current-node"
        >
          {parent.kind === 'udt' ? (
            <Boxes className="size-3.5 shrink-0 text-brand" aria-hidden />
          ) : (
            <FolderOpen className="size-3.5 shrink-0 text-brand" aria-hidden />
          )}
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">{parent.name}</span>
          {parent.kind === 'udt' ? (
            <span className="shrink-0 rounded bg-brand-soft px-1 py-0.5 text-8 font-semibold uppercase text-brand">
              UDT
            </span>
          ) : null}
          <span className="font-mono text-10 text-ink-3">
            {parentCountUnavailable ? '—' : parent.subtreeTagCount.toLocaleString()}
          </span>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-1.5 pb-2 pt-0" role="tree">
        {isLoading ? (
          <p className="m-0 px-2 py-3 text-xs text-ink-3">Loading this level…</p>
        ) : folders.length > 0 ? (
          folders.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              countUnavailable={countUnavailableIds.has(folder.id)}
              onNavigate={onNavigate}
              onSelectAll={onSelectAll}
            />
          ))
        ) : parent?.folderless ? (
          <div
            className="m-1 rounded-md border border-dashed border-line p-3 text-11 text-ink-3"
            data-testid="tag-tree-folderless-message"
          >
            This provider has no folders. Its tags are listed directly.
          </div>
        ) : (
          <p className="m-0 px-2 py-3 text-xs text-ink-3">
            {filtersActive ? 'No child containers match these filters.' : 'No child containers.'}
          </p>
        )}
      </div>

      {onRegistrationIssues || onOpenTools ? (
        <div className="border-t border-line px-2 py-2">
          {onRegistrationIssues ? (
            <div>
              <p className={cn(SECTION_HEADING_CLASS, 'm-0 px-1.5 pb-1')}>Views</p>
              <button
                type="button"
                onClick={onRegistrationIssues}
                aria-pressed={activeView === 'issues'}
                className={cn(
                  'flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left font-mono text-xs hover:bg-bg-sunk/60',
                  activeView === 'issues' ? 'bg-bg-sunk text-ink' : 'text-ink-2'
                )}
                data-testid="tag-tree-registration-issues"
              >
                <Unlink className="size-3 shrink-0 text-ink-3" aria-hidden />
                <span className="flex-1">Registration issues</span>
                {registrationIssuesCount !== null && registrationIssuesCount !== undefined ? (
                  <span className="text-10 text-ink-3">
                    {registrationIssuesCount.toLocaleString()}
                  </span>
                ) : null}
                <ChevronRight className="size-3 shrink-0 text-ink-3" aria-hidden />
              </button>
            </div>
          ) : null}
          {onOpenTools ? (
            <button
              type="button"
              onClick={onOpenTools}
              className={cn(
                'flex w-full items-center gap-1.5 rounded border border-line bg-bg px-2 py-1.5 text-left font-mono text-xs text-ink-2 hover:border-brand/30 hover:bg-brand-soft',
                onRegistrationIssues && 'mt-2'
              )}
              data-testid="tag-bundle-tools-trigger"
            >
              <Wrench className="size-3 shrink-0 text-brand" aria-hidden />
              <span className="flex-1">Tools</span>
              <ChevronRight className="size-3 shrink-0 text-ink-3" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </aside>
  )
}

export default TagTreeSidebar
